import * as i0 from '@angular/core';
import { makeEnvironmentProviders, InjectionToken, isDevMode, Optional, Inject, Injectable, ɵRuntimeError as _RuntimeError } from '@angular/core';
import { tap, distinctUntilChanged, shareReplay, switchMap, mergeMap, map } from 'rxjs/operators';
import { BehaviorSubject, of, Observable, timer } from 'rxjs';
import equal from 'fast-deep-equal/es6';
import { UrlSegmentGroup, PRIMARY_OUTLET, UrlSegment, DefaultUrlSerializer, UrlTree, UrlSerializer } from '@angular/router';
function provideFoundation(...features) {
  const providers = [];
  features.forEach(feature => {
    providers.push(...feature.providers);
  });
  return makeEnvironmentProviders(providers);
}
var FeatureKind;
(function (FeatureKind) {
  FeatureKind[FeatureKind["CaseInsensitiveRouting"] = 0] = "CaseInsensitiveRouting";
  /** @deprecated Use Environment instead. This will be removed in a future release. */
  FeatureKind[FeatureKind["DefaultConfiguration"] = 1] = "DefaultConfiguration";
  FeatureKind[FeatureKind["Environment"] = 2] = "Environment";
})(FeatureKind || (FeatureKind = {}));

/**
 * @deprecated This token is temporarily introduced to distinguish between
 * a default configuration and a non-default configuration. That will no longer
 * be necessary in a future release.
 */
const EnvironmentDefaultConfigurationToken = new InjectionToken('EnvironmentDefaultConfigurationToken');
const EnvironmentConfigurationToken = new InjectionToken('EnvironmentConfigurationToken');

/**
 * A service that loads, manages, and emits an environment configuration. The default configuration
 * data refresh-rate interval is five minutes. If provided within the configuration data, an alternate
 * refresh-rate interval is applied on first-load.
 * @template T - Type of the environment configuration. Must extend IEnvironmentConfiguration.
 */
class EnvironmentService {
  /**
   * Allows for non-injection contexts where manual instantiation of this service is required.
   * @param _configuration An optional configuration. It may be an object, a JSON string, or
   * a Base64-encoded JSON string. If not provided, this service attempts to load configuration
   * data from /assets/Environment. This parameter is useful in SSR environments.
   * @param _defaultConfiguration An optional configuration specific to the development environment.
   * It may be an object, a JSON string, or a Base64-encoded JSON string. If not provided, this service
   * will first revert to using _configuration. If that does not exist, it attempts to load configuration
   * data from /assets/Environment.
   */
  /* eslint-disable @angular-eslint/prefer-inject */
  constructor(_configuration = null,
  /**
   * @deprecated The currently active configuration is now stored in /assets/Environment, so distinguishing
   * between _configuration and _defaultConfiguration is no longer necessary.
   */
  _defaultConfiguration = null) {
    this._configuration = _configuration;
    this._defaultConfiguration = _defaultConfiguration;
    this.ENVIRONMENT_FILE = '/assets/Environment.json';
    this.DEFAULT_REFRESH_RATE = 300_000;
    /**
     * Allows for a Dynamically-Loaded Environment file to be updated every refresh-rate interval.
     */
    this._refreshRateSubject = new BehaviorSubject(this.DEFAULT_REFRESH_RATE);
    /**
     * @deprecated Will be removed in a future version.
     * @description Returns true when in DevMode.
     */
    this.isUsingDefaultConfiguration$ = of(isDevMode());
    /**
     * @deprecated Will be removed in a future version. This always returns true now, since
     * loading takes place immediately following the first subscription to configuration$.
     * This observable was used to indicate when the configuration data had been loaded
     * and parsed, allowing an application to stall its loading process until that was
     * completed. This must now always report "true" to prevent applications from hanging
     * during the load process since the configuration data is now loaded as part of the
     * configuration$ subscription.
     */
    this.isLoaded$ = of(true);
    /**
     * Provides a hot observable that will emit an environment configuration whenever configuration data changes.
     * If this service is using a dynamic configuration, e.g. the /assets/Environment file, the file will be reloaded
     * and checked for any changes every five minutes. If the configuration data has changed, a new configuration
     * will be emitted. If a static configuration is used, e.g. providing the EnvironmentConfigurationToken or
     * directly instantiating this class and passing in _configuration to the constructor, this will only ever emit once.
     */
    this.configuration$ = new Observable(observer => {
      const providedConfiguration = isDevMode() && this._defaultConfiguration != null ? this._defaultConfiguration : this._configuration;
      if (providedConfiguration != null) {
        const preparedConfiguration = this.prepareConfiguration(providedConfiguration);
        if (preparedConfiguration != null) {
          observer.next(preparedConfiguration);
          return;
        }
      }
      this.getDynamicConfigurationData().pipe(tap(configuration => {
        if (configuration.refreshRate != null && configuration.refreshRate !== this._refreshRateSubject.value) {
          this._refreshRateSubject.next(configuration.refreshRate);
        }
      })).subscribe({
        next: configuration => observer.next(configuration),
        error: err => {
          const message = `Environment Error: ${JSON.stringify(err, Object.getOwnPropertyNames(err))}`;
          observer.error(new Error(message));
        }
      });
    }).pipe(distinctUntilChanged((previous, current) => equal(previous, current)), shareReplay(1));
  }
  /**
   * @deprecated This will be removed in a future version. This no longer has any effect since loading takes
   * place after the first subscription to configuration$.
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  loadConfiguration() {}
  /**
   * Fetches the environment configuration from the server every REFRESH_RATE interval.
   * @returns An observable containing JSON.parse(Base64.decode(data)).
   */
  getDynamicConfigurationData() {
    const headers = {
      'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      Pragma: 'no-cache',
      Expires: '0',
      'Content-Type': 'text/plain; charset=utf-8'
    };
    const request = {
      method: 'GET',
      headers,
      cache: 'no-cache'
    };
    // Whenever _refreshRateSubject emits, switchMap unsubscribes from timer() and resubscribes with the new rate.
    return this._refreshRateSubject.pipe(switchMap(refreshRate => timer(0, refreshRate)), mergeMap(() => fetch(this.ENVIRONMENT_FILE, request).catch(() => fetch('/assets/Environment', request))), mergeMap(response => response?.body != null ? response.text() : of(null)), map(data => {
      if (data == null) {
        throw new Error('Error Fetching Configuration File.');
      }
      if (data === '') {
        throw new Error('Configuration File is Empty.');
      }
      const configuration = this.prepareConfiguration(data);
      if (configuration == null) {
        throw new Error('Error Parsing or Decoding Configuration File.');
      }
      return configuration;
    }));
  }
  prepareConfiguration(configuration) {
    if (typeof configuration !== 'string') {
      return configuration;
    }
    let preparedConfiguration = null;
    let decodedConfiguration = null;
    try {
      preparedConfiguration = JSON.parse(configuration);
    } catch {
      decodedConfiguration = atob(configuration);
    }
    if (decodedConfiguration != null) {
      preparedConfiguration = JSON.parse(decodedConfiguration);
    }
    return preparedConfiguration;
  }
  static {
    this.ɵfac = function EnvironmentService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || EnvironmentService)(i0.ɵɵinject(EnvironmentConfigurationToken, 8), i0.ɵɵinject(EnvironmentDefaultConfigurationToken, 8));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: EnvironmentService,
      factory: EnvironmentService.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EnvironmentService, [{
    type: Injectable
  }], () => [{
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [EnvironmentConfigurationToken]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [EnvironmentDefaultConfigurationToken]
    }]
  }], null);
})();
function makeFeature(kind, providers) {
  return {
    kind,
    providers
  };
}
function withEnvironment(configuration = null) {
  return makeFeature(FeatureKind.Environment, [EnvironmentService, {
    provide: EnvironmentConfigurationToken,
    useValue: configuration
  }]);
}

/**
 * @deprecated Use withEnvironment() instead. This will be removed in a future release.
 * @param defaultConfiguration An object that implements IEnvironmentConfiguration and
 * provides application configuration settings when Angular is running in DevMode.
 */
function withDefaultConfiguration(defaultConfiguration) {
  return makeFeature(FeatureKind.DefaultConfiguration, [EnvironmentService, {
    provide: EnvironmentDefaultConfigurationToken,
    useValue: defaultConfiguration
  }]);
}

/**
 * This is from the Angular codebase but modified to set url-segments to lowercase, leaving params and fragments as-is.
 * It was a non-exported class, and we need access to its functionality in the CaseInsensitiveUrlSerializer.
 */
class CaseInsensitiveUrlParser {
  constructor(url) {
    this._url = url ?? '';
    this._remaining = url ?? '';
  }
  parseRootSegment() {
    this.consumeOptional('/');
    if (this._remaining === '' || this.peekStartsWith('?') || this.peekStartsWith('#')) {
      return new UrlSegmentGroup([], {});
    }
    // The root segment group never has segments
    return new UrlSegmentGroup([], this.parseChildren());
  }
  parseQueryParams() {
    const params = {};
    if (this.consumeOptional('?')) {
      do {
        this.parseQueryParam(params);
      } while (this.consumeOptional('&'));
    }
    return params;
  }
  parseFragment() {
    return this.consumeOptional('#') ? decodeURIComponent(this._remaining) : null;
  }
  parseChildren() {
    if (this._remaining === '') {
      return {};
    }
    this.consumeOptional('/');
    const segments = [];
    if (!this.peekStartsWith('(')) {
      segments.push(this.parseSegment());
    }
    while (this.peekStartsWith('/') && !this.peekStartsWith('//') && !this.peekStartsWith('/(')) {
      this.capture('/');
      segments.push(this.parseSegment());
    }
    let children = {};
    if (this.peekStartsWith('/(')) {
      this.capture('/');
      children = this.parseParens(true);
    }
    let res = {};
    if (this.peekStartsWith('(')) {
      res = this.parseParens(false);
    }
    if (segments.length > 0 || Object.keys(children).length > 0) {
      res[PRIMARY_OUTLET] = new UrlSegmentGroup(segments, children);
    }
    return res;
  }
  // parse a segment with its matrix parameters - lowercases the segment after decoding it
  // ie `name;k1=v1;k2`
  parseSegment() {
    const path = matchSegments(this._remaining);
    if (path === '' && this.peekStartsWith(';')) {
      throw new _RuntimeError(4009 /* RuntimeErrorCode.EMPTY_PATH_WITH_PARAMS */, isDevMode() && `Empty path url segment cannot have parameters: '${this._remaining}'.`);
    }
    this.capture(path);
    return new UrlSegment(decode(path).toLowerCase(), this.parseMatrixParams());
  }
  parseMatrixParams() {
    const params = {};
    while (this.consumeOptional(';')) {
      this.parseParam(params);
    }
    return params;
  }
  parseParam(params) {
    const key = matchMatrixKeySegments(this._remaining);
    if (!key) {
      return;
    }
    this.capture(key);
    let value = '';
    if (this.consumeOptional('=')) {
      const valueMatch = matchSegments(this._remaining);
      if (valueMatch) {
        value = valueMatch;
        this.capture(value);
      }
    }
    params[decode(key)] = decode(value);
  }
  // Parse a single query parameter `name[=value]`
  parseQueryParam(params) {
    const key = matchQueryParams(this._remaining);
    if (!key) {
      return;
    }
    this.capture(key);
    let value = '';
    if (this.consumeOptional('=')) {
      const valueMatch = matchUrlQueryParamValue(this._remaining);
      if (valueMatch) {
        value = valueMatch;
        this.capture(value);
      }
    }
    const decodedKey = decodeQuery(key);
    const decodedVal = decodeQuery(value);
    if (Object.hasOwn(params, decodedKey)) {
      // Append to existing values
      let currentVal = params[decodedKey];
      if (!Array.isArray(currentVal)) {
        currentVal = [currentVal];
        params[decodedKey] = currentVal;
      }
      currentVal.push(decodedVal);
    } else {
      // Create a new value
      params[decodedKey] = decodedVal;
    }
  }
  // parse `(a/b//outlet_name:c/d)`
  parseParens(allowPrimary) {
    const segments = {};
    this.capture('(');
    while (!this.consumeOptional(')') && this._remaining.length > 0) {
      const path = matchSegments(this._remaining);
      const next = this._remaining[path.length];
      // if it is not one of these characters, then the segment was unescaped
      // or the group was not closed
      if (next !== '/' && next !== ')' && next !== ';') {
        throw new _RuntimeError(4010 /* RuntimeErrorCode.UNPARSABLE_URL */, isDevMode() && `Cannot parse url '${this._url}'`);
      }
      let outletName = '';
      if (path.indexOf(':') > -1) {
        outletName = path.slice(0, path.indexOf(':'));
        this.capture(outletName);
        this.capture(':');
      } else if (allowPrimary) {
        outletName = PRIMARY_OUTLET;
      }
      const children = this.parseChildren();
      segments[outletName] = Object.keys(children).length === 1 ? children[PRIMARY_OUTLET] : new UrlSegmentGroup([], children);
      this.consumeOptional('//');
    }
    return segments;
  }
  peekStartsWith(str) {
    return this._remaining.startsWith(str);
  }
  // Consumes the prefix when it is present and returns whether it has been consumed
  consumeOptional(str) {
    if (this.peekStartsWith(str)) {
      this._remaining = this._remaining.substring(str.length);
      return true;
    }
    return false;
  }
  capture(str) {
    if (!this.consumeOptional(str)) {
      throw new _RuntimeError(4011 /* RuntimeErrorCode.UNEXPECTED_VALUE_IN_URL */, isDevMode() && `Expected "${str}".`);
    }
  }
}
function decode(s) {
  return decodeURIComponent(s);
}
// Query keys/values should have the "+" replaced first, as "+" in a query string is " ".
// decodeURIComponent function will not decode "+" as a space.
function decodeQuery(s) {
  return decode(s.replace(/\+/g, '%20'));
}
const SEGMENT_RE = /^[^/()?;#]+/;
function matchSegments(str) {
  const match = str.match(SEGMENT_RE);
  return match ? match[0] : '';
}
const QUERY_PARAM_VALUE_RE = /^[^&#]+/;
// Return the value of the query param at the start of the string or an empty string
function matchUrlQueryParamValue(str) {
  const match = str.match(QUERY_PARAM_VALUE_RE);
  return match ? match[0] : '';
}
const MATRIX_PARAM_SEGMENT_RE = /^[^/()?;=#]+/;
function matchMatrixKeySegments(str) {
  const match = str.match(MATRIX_PARAM_SEGMENT_RE);
  return match ? match[0] : '';
}
const QUERY_PARAM_RE = /^[^=?&#]+/;
// Return the name of the query param at the start of the string or an empty string
function matchQueryParams(str) {
  const match = str.match(QUERY_PARAM_RE);
  return match ? match[0] : '';
}

/**
 * Provides case-insensitive routing while only applying the lowercase function to url segments
 * and leaving params and fragments as they are.
 * Ex: Inputting /home/AdvancedView into the Browser's URL path routes to the same location as
 * inputting /home/advancedview.
 */
class CaseInsensitiveUrlSerializer extends DefaultUrlSerializer {
  parse(url) {
    const parser = new CaseInsensitiveUrlParser(url);
    const rootSegments = parser.parseRootSegment();
    const queryParams = parser.parseQueryParams();
    const fragments = parser.parseFragment();
    return new UrlTree(rootSegments, queryParams, fragments);
  }
}
const withCaseInsensitiveRouting = () => makeFeature(FeatureKind.CaseInsensitiveRouting, [{
  provide: UrlSerializer,
  useClass: CaseInsensitiveUrlSerializer
}]);

/**
 * Generated bundle index. Do not edit.
 */

export { CaseInsensitiveUrlSerializer, EnvironmentService, FeatureKind, makeFeature, provideFoundation, withCaseInsensitiveRouting, withDefaultConfiguration, withEnvironment };
